import React from "react";
import { WooCommerce } from "../../../src/lib/api";
import { useQuery } from "react-query";
import {
  Card,
  CardContent,
  CardTitle,
} from "../../../src/components/ui/card";
import { Separator } from "../../../src/components/ui/separator";
import { useParams } from "react-router-dom";
import MyLoader from "../../../src/components/MyLoader";
import { wooOrder } from "./orderInterface";
import TrackShipment from "../../../src/components/trackShipment";

// Extracted shipment service to a separate function
export default function Order() {
  const { orderId, orderReference } = useParams();
  const { data: order, isLoading } = useQuery(
    ["order", orderId],
    async () => {
      const response = await WooCommerce.get(
        `orders/${orderId}`
      );
      if (!response?.data) throw new Error("No order data");
      document.cookie =
        "cartKey=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;";
      localStorage.removeItem("cart_key");
      return response.data;
    },
    {
      enabled: !!orderId,
      retry: false,
      onError: (error) => {
        console.error("Order processing error:", error);
      },
    }
  );

  if (isLoading) return <MyLoader />;

  return (
    <div className="mt-16 sm:mt-36 sm:p-10 p-5 font-montserrat flex flex-col gap-10">
      <h2 className="text-xl sm:text-2xl font-medium mb-10">
        Thank you, your order has been successful
      </h2>
      <OrderSummary
        order={order}
        orderReference={orderReference || ""}
        paymentMethod={order.payment_method}
        orderDate={order.date_created}
      />
    </div>
  );
}

// Extracted Order Summary component for better readability
function OrderSummary({
  order,
  paymentMethod,
  orderDate,
  orderReference,
}: {
  order: wooOrder;
  paymentMethod: string;
  orderDate: string;
  orderReference: string;
}) {
  return (
    <>
      <div className="flex sm:flex-row flex-col sm:flex-nowrap flex-wrap justify-between items-start gap-5">
        <OrderDetail
          label="Order Number"
          value={order?.number}
        />
        <OrderDetail
          label="Date"
          value={order?.date_created}
        />
        <OrderDetail
          label="Payment Method"
          value={order?.payment_method}
          isBadge
        />
      </div>
      <TrackShipment orderReference={orderReference} />
      <div className="flex sm:flex-row flex-col justify-between items-stretch gap-10 h-fit mt-5">
        <CustomerDetailsCard order={order} />
        <OrderItemsCard order={order} />
      </div>
    </>
  );
}

// Reusable Order Detail component
function OrderDetail({
  label,
  value,
  isBadge = false,
}: {
  label: string;
  value: string;
  isBadge?: boolean;
}) {
  return (
    <div className="flex flex-col gap-2 justify-center items-start border-neutral-600 sm:w-full w-fit">
      <h2 className="font-bold sm:text-md text-sm">
        {label}
      </h2>

      <p className="font-light text-sm rounded-none italic">
        {value}
      </p>
    </div>
  );
}

// Customer Details Card component
function CustomerDetailsCard({
  order,
}: {
  order: wooOrder;
}) {
  const customerName = `${
    order?.billing?.first_name || ""
  } ${order?.billing?.last_name || ""}`.trim();

  return (
    <Card className="w-full text-sm rounded-none border-none shadow-none">
      <CardTitle>Customer Details</CardTitle>
      <Separator className="mt-2 mb-2" />
      <CardContent className="flex gap-2 flex-col p-0">
        <DetailRow
          label="Name"
          value={customerName || "N/A"}
        />
        <DetailRow
          label="Address"
          value={order?.billing?.address_1 || "N/A"}
        />
        <DetailRow
          label="City"
          value={order?.billing?.city || "N/A"}
        />
        <DetailRow
          label="Country"
          value={order?.billing?.country || "N/A"}
        />
        <DetailRow
          label="Email"
          value={order?.billing?.email || "N/A"}
        />
        <DetailRow
          label="Phone"
          value={order?.billing?.phone || "N/A"}
        />
      </CardContent>
    </Card>
  );
}

// Order Items Card component
function OrderItemsCard({ order }: { order: wooOrder }) {
  const subtotal =
    order?.line_items?.reduce(
      (acc, item) => acc + Number(item.subtotal),
      0
    ) || 0;

  return (
    <Card className="w-full border-0 text-sm rounded-none">
      <CardTitle>Order Details</CardTitle>
      <Separator className="mt-2 mb-2" />
      <CardContent className="flex gap-2 flex-col p-0">
        {order?.line_items?.map((item: any) => (
          <div
            key={item.id}
            className="flex justify-between items-center gap-2"
          >
            <span>
              {item.name} x{item.quantity}
            </span>
            <span>{item.price}.00$</span>
          </div>
        ))}
        <Separator />
        <PriceBreakdown
          subtotal={subtotal}
          shipping={order?.shipping_total}
          discount={order?.discount_total}
          total={order?.total}
        />
      </CardContent>
    </Card>
  );
}

// Price Breakdown component
function PriceBreakdown({
  subtotal,
  shipping,
  discount,
  total,
}: {
  subtotal: number;
  shipping: string;
  discount: string;
  total: string;
}) {
  return (
    <ul className="grid gap-3">
      <PriceRow label="Subtotal" value={`${subtotal}$`} />
      <PriceRow label="Shipping" value={`${shipping}$`} />
      <PriceRow
        label="Promo Code Discount"
        value={`${discount}$`}
      />
      <PriceRow label="Total" value={`${total}$`} isTotal />
    </ul>
  );
}

// Reusable Detail Row component
function DetailRow({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="flex justify-between items-center w-full">
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

// Reusable Price Row component
function PriceRow({
  label,
  value,
  isTotal = false,
}: {
  label: string;
  value: string;
  isTotal?: boolean;
}) {
  return (
    <li className="flex items-center justify-between">
      <span
        className={
          isTotal ? "font-medium" : "text-muted-foreground"
        }
      >
        {label}
      </span>
      <span className={isTotal ? "font-medium" : ""}>
        {value}
      </span>
    </li>
  );
}
